import React, { useMemo, useState } from 'react';
import { Card, Table, Button, Select, Tag, Space, Popconfirm, App, Input, DatePicker } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs, { type Dayjs } from 'dayjs';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supplierQuotationService, type SupplierQuotation } from '@/services/supplierQuotation.service';
import { supplierInquiryService } from '@/services/supplierInquiry.service';

const { RangePicker } = DatePicker;

const SupplierQuotationsPage: React.FC = () => {
  const navigate = useNavigate();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [inquiryFilter, setInquiryFilter] = useState<string | undefined>(undefined);
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);

  const { data: inquiries = [] } = useQuery({
    queryKey: ['supplier-inquiries-list'],
    queryFn: () => supplierInquiryService.list({}),
  });

  const { data: quotations = [], isLoading } = useQuery({
    queryKey: ['supplier-quotations', inquiryFilter],
    queryFn: () => supplierQuotationService.list(inquiryFilter ? { supplier_inquiry_id: inquiryFilter } : {}),
  });

  const inquiryNumberById = useMemo(() => {
    const m = new Map<string, string>();
    for (const i of inquiries) m.set(i.id, i.inquiry_number);
    return m;
  }, [inquiries]);

  const statusOptions = useMemo(
    () =>
      Array.from(new Set(quotations.map((q) => q.status).filter(Boolean)))
        .sort()
        .map((s) => ({ value: s, label: s })),
    [quotations]
  );

  const filteredQuotations = useMemo(() => {
    const term = searchText.trim().toLowerCase();
    const [from, to] = dateRange ?? [null, null];
    return quotations.filter((q: SupplierQuotation) => {
      if (statusFilter && q.status !== statusFilter) return false;
      if (from || to) {
        if (!q.quotation_date) return false;
        const d = dayjs(q.quotation_date);
        if (from && d.isBefore(from, 'day')) return false;
        if (to && d.isAfter(to, 'day')) return false;
      }
      if (!term) return true;
      const haystack = [
        q.quotation_number,
        q.supplier_quotation_ref,
        q.supplier?.supplier_name,
        q.supplier?.supplier_code,
        inquiryNumberById.get(q.supplier_inquiry_id),
        q.status,
        q.remarks,
        ...(q.lines ?? []).flatMap((l) => [l.item_code, l.item_description]),
      ];
      return haystack.some((v) => v && String(v).toLowerCase().includes(term));
    });
  }, [quotations, searchText, statusFilter, dateRange, inquiryNumberById]);

  const deleteMutation = useMutation({
    mutationFn: (qid: string) => supplierQuotationService.delete(qid),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['supplier-quotations'] });
      queryClient.invalidateQueries({ queryKey: ['supplier-quotations-by-inquiry'] });
      message.success('Quotation deleted');
    },
    onError: (e: any) => {
      message.error(e?.response?.data?.message || e?.message || 'Delete failed');
    },
  });

  const hasFilters = Boolean(searchText || statusFilter || inquiryFilter || dateRange);

  return (
    <div style={{ padding: 24 }}>
      <Card title="Supplier quotations">
        <Space wrap style={{ marginBottom: 16 }}>
          <Input
            allowClear
            prefix={<SearchOutlined />}
            placeholder="Search quotation #, supplier ref, supplier, inquiry, item"
            style={{ width: 360 }}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
          <Select
            placeholder="Filter by inquiry"
            allowClear
            showSearch
            optionFilterProp="label"
            style={{ width: 260 }}
            value={inquiryFilter}
            onChange={setInquiryFilter}
            options={inquiries.map((i) => ({ value: i.id, label: `${i.inquiry_number} (${i.inquiry_date ? dayjs(i.inquiry_date).format('DD MMM YYYY') : '-'})` }))}
          />
          <Select
            placeholder="Status"
            allowClear
            style={{ width: 160 }}
            value={statusFilter}
            onChange={setStatusFilter}
            options={statusOptions}
          />
          <RangePicker
            value={dateRange}
            onChange={(v) => setDateRange(v as [Dayjs | null, Dayjs | null] | null)}
            format="DD MMM YYYY"
            allowEmpty={[true, true]}
          />
          {hasFilters ? (
            <Button
              onClick={() => {
                setSearchText('');
                setStatusFilter(undefined);
                setInquiryFilter(undefined);
                setDateRange(null);
              }}
            >
              Clear filters
            </Button>
          ) : null}
        </Space>
        <Table
          size="small"
          rowKey="id"
          loading={isLoading}
          dataSource={filteredQuotations}
          columns={[
            {
              title: 'Quotation #',
              dataIndex: 'quotation_number',
              width: 140,
              sorter: (a: SupplierQuotation, b: SupplierQuotation) =>
                String(a.quotation_number ?? '').localeCompare(String(b.quotation_number ?? '')),
            },
            { title: 'Supplier ref', dataIndex: 'supplier_quotation_ref', width: 140, render: (v: string) => v || '-' },
            {
              title: 'Inquiry',
              key: 'inquiry',
              width: 150,
              render: (_: any, r: SupplierQuotation) =>
                r.supplier_inquiry_id ? (
                  <Button type="link" size="small" style={{ paddingInline: 0 }} onClick={() => navigate(`/scm/procurement/supplier-inquiries/${r.supplier_inquiry_id}`)}>
                    {inquiryNumberById.get(r.supplier_inquiry_id) ?? 'View inquiry'}
                  </Button>
                ) : (
                  '-'
                ),
            },
            {
              title: 'Supplier',
              key: 'supplier',
              sorter: (a: SupplierQuotation, b: SupplierQuotation) =>
                String(a.supplier?.supplier_name ?? '').localeCompare(String(b.supplier?.supplier_name ?? '')),
              render: (_: any, r: SupplierQuotation) => r.supplier?.supplier_name ?? r.supplier_id,
            },
            {
              title: 'Date',
              dataIndex: 'quotation_date',
              width: 110,
              sorter: (a: SupplierQuotation, b: SupplierQuotation) =>
                dayjs(a.quotation_date ?? 0).valueOf() - dayjs(b.quotation_date ?? 0).valueOf(),
              render: (d: string) => (d ? dayjs(d).format('DD MMM YYYY') : '-'),
            },
            { title: 'Status', dataIndex: 'status', width: 100, render: (s: string) => <Tag color={s === 'Submitted' ? 'green' : 'default'}>{s}</Tag> },
            {
              title: 'Actions',
              width: 200,
              render: (_: any, row: SupplierQuotation) => (
                <Space size="small">
                  <Button type="link" size="small" onClick={() => navigate(`/scm/procurement/supplier-quotations/${row.id}`)}>
                    View
                  </Button>
                  <Popconfirm
                    title="Delete this quotation?"
                    description="Not allowed if used in bid analysis or on a purchase order."
                    okText="Delete"
                    okButtonProps={{ danger: true }}
                    disabled={deleteMutation.isPending}
                    onConfirm={() => deleteMutation.mutate(row.id)}
                  >
                    <Button type="link" size="small" danger loading={deleteMutation.isPending}>
                      Delete
                    </Button>
                  </Popconfirm>
                </Space>
              ),
            },
          ]}
          pagination={{
            defaultPageSize: 20,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            showTotal: (total) => `${total} quotation${total === 1 ? '' : 's'}`,
          }}
        />
      </Card>
    </div>
  );
};

export default SupplierQuotationsPage;
